import React, { useState, useEffect } from 'react';
import useMediaUpload from '../hooks/useMediaUpload';
import useProfileFormData from '../hooks/useProfileFormData';
import useRoleNormalization from '../hooks/useRoleNormalization';
import { Formik, Form } from 'formik';
import { Grid, Header, Button, Message, Popup, Loader } from 'semantic-ui-react';
import { allStates } from '../constants/config';
import './BookCreatorProfileForm.css';
import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikField
} from '../pages/subPages/voiceActor/FormComponents';
import {
  ProfileFormHeader
} from '../common';

/**
 * BookCreatorProfileForm component for editing book creator profiles
 * Uses the centralized useProfileFormData hook for deterministic initialization
 * @param {Object} props - Component props
 * @param {Object} props.profile - Profile data
 * @param {string} props.userId - User ID of the profile being edited
 * @param {Object} props.initialUserData - Data from Add New User flow
 * @param {Function} props.onSave - Callback function called after successful save
 * @returns {JSX.Element} BookCreatorProfileForm component
 */
const BookCreatorProfileForm = ({ profile, userId, initialUserData, onSave }) => {
  // Use the centralized hook for form data management
  const {
    formState,
    initialValues,
    isDirty,
    error,
    saveStatus,
    targetUserId,
    operationMode,
    userInfo,
    isEditable,
    handleSave: saveProfile,
    handleFieldChange
  } = useProfileFormData({
    profileType: 'bookCreator',
    userId,
    profile,
    initialUserData,
    onSave
  });

  // Get normalization functions
  const {
    normalizeFormDataForSave,
    normalizeFormDataForDisplay,
    generateValidationSchema,
    getImageConfig
  } = useRoleNormalization();

  // Use our custom hook for media uploads
  const { uploadMedia, isUploading, error: uploadError, reset: resetUpload } = useMediaUpload();
  const [successMessage, setSuccessMessage] = useState('');

  // Get image config for this profile type
  const imageConfig = getImageConfig('bookCreator');

  console.log(`BookCreatorProfileForm - Using targetUserId: ${targetUserId} for profile operations`);
  console.log(`BookCreatorProfileForm - Operation mode: ${operationMode}`);
  console.log(`BookCreatorProfileForm - Form state: ${formState}`);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Update success message when save status changes
  useEffect(() => {
    if (saveStatus === 'created') {
      setSuccessMessage('Profile created successfully!');
    } else if (saveStatus === 'updated') {
      setSuccessMessage('Profile updated successfully!');
    }
  }, [saveStatus]);

  // Handle form submission
  const handleSubmit = async (values) => {
    console.log('BookCreatorProfileForm - handleSubmit called with values:', values);
    resetUpload();
    setSuccessMessage('');

    try {
      // Handle profile image upload if there's a new file
      let profilePictureUrl = values.profileImageUrl;

      // If hasNewImageFile is true and we have a file, upload it
      if (values.hasNewImageFile && values.profileImageFile && profile?.uuid) {
        const file = values.profileImageFile;

        try {
          profilePictureUrl = await uploadMedia(
            file,
            imageConfig.resourceType,
            {
              [imageConfig.idKey]: profile.uuid,
              resourceType: imageConfig.resourceType
            },
            targetUserId
          );
        } catch (uploadError) {
          console.error('Error uploading profile image:', uploadError);
          return;
        }
      }

      // Use normalization to prepare form data
      // Update the profileImageUrl field with new URL so normalization maps it correctly
      const dataWithImage = {
        ...values,
        profileImageUrl: profilePictureUrl  // Update the form field that gets mapped to profilePictureUrl
      };

      const formData = normalizeFormDataForSave(dataWithImage, 'bookCreator');
      console.log('BookCreatorProfileForm - Normalized formData being sent:', formData);

      // Use the hook's save function
      await saveProfile(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // Convert initialValues to Formik format using normalization
  const normalizedInitialValues = normalizeFormDataForDisplay(initialValues, 'bookCreator');
  const formikInitialValues = {
    ...normalizedInitialValues,
    profileImageUrl: initialValues.profilePictureUrl ? initialValues.profilePictureUrl.split('?')[0] : '',
    profileImageFile: null,
    hasNewImageFile: false
  };

  console.log('📝 BookCreatorProfileForm - initialValues from hook:', initialValues);
  console.log('📝 BookCreatorProfileForm - formikInitialValues:', formikInitialValues);
  console.log('📝 BookCreatorProfileForm - firstName:', formikInitialValues.firstName);
  console.log('📝 BookCreatorProfileForm - lastName:', formikInitialValues.lastName);

  // Show loader while initializing
  // if (formState === 'initializing') {
  //   return <div></div>; // <Loader active inline='centered'>Loading profile...</Loader>;
  // }

  // Show error if any
  if (formState === 'error' && error) {
    return (
      <Message negative>
        <Message.Header>Error Loading Profile</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  return (
    <div style={{ border: "none", paddingRight: "20px" }}>
      <Formik
        key={`${targetUserId}-${profile?.uuid || 'new'}-${operationMode}`} // Force reinit only on significant changes
        enableReinitialize={false}
        initialValues={formikInitialValues}
        validationSchema={generateValidationSchema('bookCreator')}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, isValid, dirty: formikDirty }) => {
          // Diagnostic logs
          console.log('📝 BookCreatorProfileForm render:', {
            isDirty,          // from our hook
            formikDirty,      // from Formik
            isValid,
            isEditable,
            formState,
            hasFirstName: !!values.firstName,
            hasLastName: !!values.lastName
          });

          // Log button disabled state
          const isButtonDisabled = formState === 'updating' ||
            !values.firstName || !values.lastName || !isDirty || !isValid;
          console.log('🔘 Update button disabled?', isButtonDisabled, {
            updating: formState === 'updating',
            noFirstName: !values.firstName,
            noLastName: !values.lastName,
            notDirty: !isDirty,
            notValid: !isValid
          });

          return (
            <Form>
              <Grid divided={false}>
                {/* Profile Header with Image Upload */}
                <ProfileFormHeader
                  title="Book Creator Profile"
                  profile={profile}
                  userProfile={{ data: userInfo }}
                  isUploading={isUploading}
                  uploadError={uploadError}
                  successMessage={successMessage}
                  urlFieldName="profileImageUrl"
                  fileFieldName="profileImageFile"
                  flagFieldName="hasNewImageFile"
                  imageLabel="Creator Image"
                  onChange={handleFieldChange}
                />

                {/* Basic Information */}
                <Grid.Row>
                  <Grid.Column width={8}>
                    <FormikField
                      name="firstName"
                      component={FormikInput}
                      label="First Name"
                      fluid
                      placeholder="First Name"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <FormikField
                      name="lastName"
                      component={FormikInput}
                      label="Last Name"
                      fluid
                      placeholder="Last Name"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                </Grid.Row>

                {/* Contact Information */}
                <Grid.Row className='noTopPad'>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Contact Information</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className='noTopBottomPad'>
                  <Grid.Column width={16}>
                    <FormikField
                      name="street"
                      component={FormikInput}
                      label="Street Address"
                      fluid
                      placeholder="123 Main St"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className='noTopBottomPad'>
                  <Grid.Column width={6}>
                    <FormikField
                      name="city"
                      component={FormikInput}
                      label="City"
                      fluid
                      placeholder="City"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <FormikField
                      name="state"
                      component={FormikSelect}
                      label="State"
                      options={allStates}
                      fluid
                      placeholder="State"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <FormikField
                      name="zipCode"
                      component={FormikInput}
                      label="Zip Code"
                      fluid
                      placeholder="12345"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className='noTopBottomPad'>
                  <Grid.Column width={16}>
                    <FormikField
                      name="phones"
                      component={FormikInput}
                      label="Phone Numbers (comma-separated)"
                      fluid
                      placeholder="e.g. 555-123-4567, 555-987-6543"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                </Grid.Row>

                {/* Profile Information */}
                <Grid.Row className=''>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Profile Information</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column width={16}>
                    <FormikField
                      name="description"
                      component={FormikTextArea}
                      label="Description"
                      placeholder="Enter a brief description"
                      rows={3}
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noBottomPad">
                  <Grid.Column width={4}>
                    <FormikField
                      name="publishedBooks"
                      component={FormikInput}
                      label="Published Books"
                      type="number"
                      min={0}
                      fluid
                      placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormikField
                      name="completedProjects"
                      component={FormikInput}
                      label="Completed Projects"
                      type="number"
                      min={0}
                      fluid
                      placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormikField
                      name="rating"
                      component={FormikInput}
                      label="Rating (0-5)"
                      type="number"
                      min={0}
                      max={5}
                      step={0.1}
                      fluid
                      placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormikField
                      name="reviewCount"
                      component={FormikInput}
                      label="Review Count"
                      type="number"
                      min={0}
                      fluid
                      placeholder="0"
                      onFocus={(e) => e.target.select()}
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                </Grid.Row>

                {/* Genres and Specialties */}
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header as="h4" dividing>Genres and Specialties</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column width={16}>
                    <FormikField
                      name="genres"
                      component={FormikInput}
                      label="Genres (comma-separated)"
                      fluid
                      placeholder="e.g. Fiction, Non-fiction, Mystery, Romance"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="noTopBottomPad">
                  <Grid.Column width={16}>
                    <FormikField
                      name="specialties"
                      component={FormikInput}
                      label="Specialties (comma-separated)"
                      fluid
                      placeholder="e.g. Children's Books, Technical Writing, Poetry"
                      onChange={handleFieldChange}
                      disabled={!isEditable}
                    />
                  </Grid.Column>
                </Grid.Row>

                {/* Form Buttons */}
                {isEditable && (
                  <Grid.Row className="noTopPad">
                    <Grid.Column width={16}>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <Popup
                          content={
                            formState === 'updating' ? 'Submitting...' :
                              !values.firstName ? 'First name is required' :
                                !values.lastName ? 'Last name is required' :
                                  !isValid ? 'Please fix validation errors' :
                                    !isDirty ? 'No changes to save' :
                                      'Ready to submit'
                          }
                          trigger={
                            <span>
                              <Button
                                primary
                                type="button"  // Changed to button to avoid Formik's submit chain
                                loading={formState === 'updating'}
                                onClick={() => {
                                  console.log('Update Profile button clicked - Direct submission');
                                  // Directly call handleSubmit with the current values
                                  handleSubmit(values);
                                }}
                                disabled={
                                  formState === 'updating' ||
                                  !values.firstName ||
                                  !values.lastName ||
                                  !isDirty ||
                                  !isValid
                                }
                              >
                                Update Profile
                              </Button>
                            </span>
                          }
                          position="top left"
                          size="tiny"
                        />

                        {/* Only show the Cancel Changes button when changes have been made */}
                        {isDirty && (
                          <Popup
                            content="Discard all changes and reset the form"
                            trigger={
                              <Button
                                type="reset"
                                disabled={formState === 'updating'}
                              >
                                Cancel Changes
                              </Button>
                            }
                            position="top left"
                            size="tiny"
                          />
                        )}
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                )}

                {/* Show message if not editable */}
                {!isEditable && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Message info>
                        <Message.Header>View Only</Message.Header>
                        <p>You do not have permission to edit this profile.</p>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                )}
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default BookCreatorProfileForm;
